import { isSafePublicMediaUrl } from "./media-url";

export const CONFIGURATOR_COORDINATE_SCALE = 1_000_000 as const;

export type PublicConfiguratorFieldType =
  | "short-text"
  | "integer"
  | "measurement"
  | "choice"
  | "boolean";

export type PublicConfiguratorCanonicalUnit = "none" | "count" | "mm" | "gram";

export interface PublicConfiguratorChoice {
  id: string;
  fieldId: string;
  key: string;
  position: number;
  surchargeMinor: number;
  calculationValue: number;
  label: string;
  description?: string;
}

export interface PublicConfiguratorField {
  id: string;
  key: string;
  type: PublicConfiguratorFieldType;
  required: boolean;
  position: number;
  canonicalUnit: PublicConfiguratorCanonicalUnit;
  minimumScaled: number | null;
  maximumScaled: number | null;
  stepScaled: number | null;
  maximumLength: number | null;
  baseSurchargeMinor: number;
  variantIds: string[];
  label: string;
  helpText?: string;
  placeholder?: string;
  choices: PublicConfiguratorChoice[];
}

export interface PublicConfiguratorTemplate {
  id: string;
  key: string;
  position: number;
  surfaceId: string;
  variantId: string | null;
  mediaId: number;
  surchargeMinor: number;
  minimumWidthPpm: number;
  maximumWidthPpm: number;
  aspectWidth: number;
  aspectHeight: number;
  label: string;
  description?: string;
  imageUrl?: string;
}

export interface PublicConfiguratorSurface {
  id: string;
  key: string;
  position: number;
  variantId: string | null;
  baseMediaId: number;
  xPpm: number;
  yPpm: number;
  widthPpm: number;
  heightPpm: number;
  physicalWidthMm: number;
  physicalHeightMm: number;
  required: boolean;
  maximumLayers: number;
  label: string;
  helpText?: string;
  imageUrl?: string;
  templates: PublicConfiguratorTemplate[];
}

export interface PublicProductConfigurator {
  revisionId: string;
  version: number;
  maxSurchargeMinor: number;
  fields: PublicConfiguratorField[];
  surfaces: PublicConfiguratorSurface[];
}

export interface ClientArtworkSelection {
  surfaceId: string;
  templateId: string;
  layer: number;
  xPpm: number;
  yPpm: number;
  widthPpm: number;
  rotationMilliDegrees: 0;
}

export type ClientConfiguratorScalar = string | number | boolean;

export interface ClientProductConfigurationSelection {
  fields: Record<string, ClientConfiguratorScalar>;
  artworks: ClientArtworkSelection[];
}

export interface ConfiguratorSelectionIssue {
  path: string;
  message: string;
}

const UNSAFE_PLAIN_TEXT = /[<>\p{Cc}\u061c\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]/u;

/** Public previews may only use a CMS-owned upload route, never a remote URL. */
export function isSafeConfiguratorUploadUrl(value: unknown): value is string {
  return isSafePublicMediaUrl(value);
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Rejects malformed public data before the interactive configurator renders it. */
export function normalizePublicProductConfigurator(value: unknown): PublicProductConfigurator | null {
  if (!record(value) || typeof value.revisionId !== "string" || !Array.isArray(value.fields) || !Array.isArray(value.surfaces)) return null;
  if (!Number.isSafeInteger(value.version) || Number(value.version) < 1 || !Number.isSafeInteger(value.maxSurchargeMinor) || Number(value.maxSurchargeMinor) < 0) return null;
  const validField = (candidate: unknown) => {
    if (!record(candidate) || typeof candidate.id !== "string" || typeof candidate.key !== "string" || typeof candidate.label !== "string") return false;
    if (!["short-text", "integer", "measurement", "choice", "boolean"].includes(String(candidate.type))) return false;
    if (!Array.isArray(candidate.variantIds) || !candidate.variantIds.every((id) => typeof id === "string") || !Array.isArray(candidate.choices)) return false;
    return candidate.choices.every((choice) => record(choice) && typeof choice.id === "string" && typeof choice.key === "string" && typeof choice.label === "string");
  };
  const validSurface = (candidate: unknown) => {
    if (!record(candidate) || typeof candidate.id !== "string" || typeof candidate.key !== "string" || typeof candidate.label !== "string" || !Array.isArray(candidate.templates)) return false;
    if (candidate.imageUrl !== undefined && !isSafeConfiguratorUploadUrl(candidate.imageUrl)) return false;
    return candidate.templates.every((template) => record(template)
      && typeof template.id === "string"
      && typeof template.key === "string"
      && typeof template.label === "string"
      && (template.imageUrl === undefined || isSafeConfiguratorUploadUrl(template.imageUrl)));
  };
  if (!value.fields.every(validField) || !value.surfaces.every(validSurface)) return null;
  return value as unknown as PublicProductConfigurator;
}

function finiteInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value);
}

function clampInteger(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, Math.round(value)));
}

export function fieldAppliesToVariant(field: PublicConfiguratorField, variantId: string): boolean {
  return field.variantIds.length === 0 || field.variantIds.includes(variantId);
}

export function surfaceAppliesToVariant(surface: PublicConfiguratorSurface, variantId: string): boolean {
  return surface.variantId === null || surface.variantId === variantId;
}

export function templateAppliesToVariant(template: PublicConfiguratorTemplate, variantId: string): boolean {
  return template.variantId === null || template.variantId === variantId;
}

export function applicableConfiguratorFields(
  configurator: PublicProductConfigurator,
  variantId: string,
): PublicConfiguratorField[] {
  return configurator.fields
    .filter((field) => fieldAppliesToVariant(field, variantId))
    .sort((left, right) => left.position - right.position || left.key.localeCompare(right.key));
}

export function applicableConfiguratorSurfaces(
  configurator: PublicProductConfigurator,
  variantId: string,
): PublicConfiguratorSurface[] {
  return configurator.surfaces
    .filter((surface) => surfaceAppliesToVariant(surface, variantId))
    .map((surface) => ({
      ...surface,
      templates: surface.templates
        .filter((template) => templateAppliesToVariant(template, variantId))
        .sort((left, right) => left.position - right.position || left.key.localeCompare(right.key)),
    }))
    .sort((left, right) => left.position - right.position || left.key.localeCompare(right.key));
}

export function emptyProductConfigurationSelection(): ClientProductConfigurationSelection {
  return { fields: {}, artworks: [] };
}

/** Height uses the exact ceiling rule used by the authoritative server core. */
export function artworkHeightPpm(
  widthPpm: number,
  template: Pick<PublicConfiguratorTemplate, "aspectWidth" | "aspectHeight">,
): number {
  if (
    !finiteInteger(widthPpm) || widthPpm < 0 ||
    !finiteInteger(template.aspectWidth) || template.aspectWidth <= 0 ||
    !finiteInteger(template.aspectHeight) || template.aspectHeight <= 0
  ) return 0;
  return Number(
    (BigInt(widthPpm) * BigInt(template.aspectHeight) + BigInt(template.aspectWidth) - 1n) /
      BigInt(template.aspectWidth),
  );
}

export function maximumFittingArtworkWidthPpm(
  template: Pick<PublicConfiguratorTemplate, "maximumWidthPpm" | "aspectWidth" | "aspectHeight">,
): number {
  if (
    !finiteInteger(template.maximumWidthPpm) ||
    !finiteInteger(template.aspectWidth) || template.aspectWidth <= 0 ||
    !finiteInteger(template.aspectHeight) || template.aspectHeight <= 0
  ) return 0;
  const byHeight = Number(
    (BigInt(CONFIGURATOR_COORDINATE_SCALE) * BigInt(template.aspectWidth)) /
      BigInt(template.aspectHeight),
  );
  return Math.min(CONFIGURATOR_COORDINATE_SCALE, template.maximumWidthPpm, byHeight);
}

export function templateFitsSurface(template: PublicConfiguratorTemplate): boolean {
  return finiteInteger(template.minimumWidthPpm) &&
    template.minimumWidthPpm > 0 &&
    template.minimumWidthPpm <= maximumFittingArtworkWidthPpm(template);
}

/** Clamps customer-controlled geometry to a valid, contained v1 placement. */
export function clampArtworkPlacement(
  artwork: ClientArtworkSelection,
  template: PublicConfiguratorTemplate,
): ClientArtworkSelection | null {
  if (!templateFitsSurface(template)) return null;
  const maximumWidth = maximumFittingArtworkWidthPpm(template);
  const widthPpm = clampInteger(artwork.widthPpm, template.minimumWidthPpm, maximumWidth);
  const heightPpm = artworkHeightPpm(widthPpm, template);
  return {
    surfaceId: artwork.surfaceId,
    templateId: artwork.templateId,
    layer: clampInteger(artwork.layer, 0, 15),
    xPpm: clampInteger(artwork.xPpm, 0, CONFIGURATOR_COORDINATE_SCALE - widthPpm),
    yPpm: clampInteger(artwork.yPpm, 0, CONFIGURATOR_COORDINATE_SCALE - heightPpm),
    widthPpm,
    rotationMilliDegrees: 0,
  };
}

export function createCenteredArtwork(
  surface: PublicConfiguratorSurface,
  template: PublicConfiguratorTemplate,
  layer: number,
): ClientArtworkSelection | null {
  if (template.surfaceId !== surface.id || layer < 0 || layer >= surface.maximumLayers || !templateFitsSurface(template)) {
    return null;
  }
  const maximumWidth = maximumFittingArtworkWidthPpm(template);
  const preferredWidth = clampInteger(400_000, template.minimumWidthPpm, maximumWidth);
  const heightPpm = artworkHeightPpm(preferredWidth, template);
  return {
    surfaceId: surface.id,
    templateId: template.id,
    layer,
    xPpm: Math.floor((CONFIGURATOR_COORDINATE_SCALE - preferredWidth) / 2),
    yPpm: Math.floor((CONFIGURATOR_COORDINATE_SCALE - heightPpm) / 2),
    widthPpm: preferredWidth,
    rotationMilliDegrees: 0,
  };
}

export function moveArtworkWithKeyboard(
  artwork: ClientArtworkSelection,
  template: PublicConfiguratorTemplate,
  key: string,
  largeStep = false,
): ClientArtworkSelection | null {
  const step = largeStep ? 50_000 : 10_000;
  const delta = {
    ArrowLeft: [-step, 0],
    ArrowRight: [step, 0],
    ArrowUp: [0, -step],
    ArrowDown: [0, step],
  }[key] as [number, number] | undefined;
  if (!delta) return null;
  return clampArtworkPlacement({ ...artwork, xPpm: artwork.xPpm + delta[0], yPpm: artwork.yPpm + delta[1] }, template);
}

export function ppmToPercent(value: number): number {
  return Math.round((value / 10_000) * 100) / 100;
}

export function percentToPpm(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return clampInteger(value * 10_000, 0, CONFIGURATOR_COORDINATE_SCALE);
}

function isMissing(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

function fieldIssue(field: PublicConfiguratorField, value: unknown): string | null {
  if (isMissing(value)) return field.required ? `${field.label} is verplicht.` : null;
  if (field.type === "short-text") {
    if (typeof value !== "string" || UNSAFE_PLAIN_TEXT.test(value.normalize("NFKC"))) return `${field.label} bevat ongeldige tekens.`;
    const normalized = value.normalize("NFKC").replace(/\s+/gu, " ").trim();
    if (!normalized) return field.required ? `${field.label} is verplicht.` : null;
    if (Array.from(normalized).length > (field.maximumLength ?? 0)) return `${field.label} is te lang.`;
    return null;
  }
  if (field.type === "integer" || field.type === "measurement") {
    if (!finiteInteger(value)) return `${field.label} moet een heel getal zijn.`;
    const minimum = field.minimumScaled ?? 0;
    const maximum = field.maximumScaled ?? Number.MAX_SAFE_INTEGER;
    const step = field.stepScaled ?? 1;
    if (value < minimum || value > maximum || (value - minimum) % step !== 0) return `${field.label} valt buiten het toegestane bereik.`;
    return null;
  }
  if (field.type === "choice") {
    if (typeof value !== "string" || !field.choices.some((choice) => choice.key === value)) return `Kies een geldige waarde voor ${field.label}.`;
    return null;
  }
  return typeof value === "boolean" ? null : `${field.label} moet aan of uit zijn.`;
}

export function configurationSelectionIssues(
  configurator: PublicProductConfigurator,
  variantId: string,
  selection: ClientProductConfigurationSelection,
): ConfiguratorSelectionIssue[] {
  const issues: ConfiguratorSelectionIssue[] = [];
  const allFields = new Map(configurator.fields.map((field) => [field.key, field]));
  const fields = applicableConfiguratorFields(configurator, variantId);
  const applicableKeys = new Set(fields.map((field) => field.key));

  for (const key of Object.keys(selection.fields)) {
    if (!allFields.has(key) || !applicableKeys.has(key)) issues.push({ path: `fields.${key}`, message: `Optie ${key} is niet beschikbaar voor deze variant.` });
  }
  for (const field of fields) {
    const message = fieldIssue(field, selection.fields[field.key]);
    if (message) issues.push({ path: `fields.${field.key}`, message });
  }

  const surfaces = applicableConfiguratorSurfaces(configurator, variantId);
  const surfacesById = new Map(surfaces.map((surface) => [surface.id, surface]));
  const usedLayers = new Set<string>();
  if (selection.artworks.length > 32) {
    issues.push({ path: "artworks", message: "Een productconfiguratie kan maximaal 32 opdrukken bevatten." });
  }
  for (const [index, artwork] of selection.artworks.entries()) {
    const surface = surfacesById.get(artwork.surfaceId);
    const template = surface?.templates.find((candidate) => candidate.id === artwork.templateId);
    const layerKey = `${artwork.surfaceId}:${artwork.layer}`;
    if (!surface || !template || !isSafeConfiguratorUploadUrl(template.imageUrl)) {
      issues.push({ path: `artworks.${index}`, message: "Deze opdruk is niet beschikbaar voor de gekozen variant." });
      continue;
    }
    if (!isSafeConfiguratorUploadUrl(surface.imageUrl)) {
      issues.push({ path: `artworks.${index}`, message: "De productafbeelding voor deze opdruk is niet beschikbaar." });
    }
    if (artwork.layer < 0 || artwork.layer >= surface.maximumLayers || usedLayers.has(layerKey)) {
      issues.push({ path: `artworks.${index}.layer`, message: "De laag van deze opdruk is ongeldig." });
    }
    usedLayers.add(layerKey);
    const clamped = clampArtworkPlacement(artwork, template);
    if (!clamped || clamped.xPpm !== artwork.xPpm || clamped.yPpm !== artwork.yPpm || clamped.widthPpm !== artwork.widthPpm || artwork.rotationMilliDegrees !== 0) {
      issues.push({ path: `artworks.${index}`, message: "De opdruk moet volledig binnen het bedrukbare vlak vallen." });
    }
  }
  for (const surface of surfaces) {
    if (surface.required && !selection.artworks.some((artwork) => artwork.surfaceId === surface.id)) {
      issues.push({ path: `surfaces.${surface.id}`, message: `Kies minimaal één opdruk voor ${surface.label}.` });
    }
  }
  return issues;
}

/** Drops stale variant values and safely re-clamps retained artwork. */
export function normalizeSelectionForVariant(
  configurator: PublicProductConfigurator,
  variantId: string,
  selection: ClientProductConfigurationSelection,
): ClientProductConfigurationSelection {
  const fields: Record<string, ClientConfiguratorScalar> = {};
  for (const field of applicableConfiguratorFields(configurator, variantId)) {
    const value = selection.fields[field.key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") fields[field.key] = value;
  }
  const surfaces = new Map(applicableConfiguratorSurfaces(configurator, variantId).map((surface) => [surface.id, surface]));
  const usedLayers = new Set<string>();
  const artworks: ClientArtworkSelection[] = [];
  for (const artwork of selection.artworks) {
    const surface = surfaces.get(artwork.surfaceId);
    const template = surface?.templates.find((candidate) => candidate.id === artwork.templateId);
    const layerKey = `${artwork.surfaceId}:${artwork.layer}`;
    if (!surface || !template || usedLayers.has(layerKey) || !isSafeConfiguratorUploadUrl(surface.imageUrl) || !isSafeConfiguratorUploadUrl(template.imageUrl)) continue;
    const normalized = clampArtworkPlacement(artwork, template);
    if (!normalized || normalized.layer >= surface.maximumLayers) continue;
    usedLayers.add(layerKey);
    artworks.push(normalized);
  }
  return { fields, artworks };
}

export type ConfiguratorEditorFieldType = PublicConfiguratorFieldType;

export interface ConfiguratorEditorChoiceDraft {
  clientId: string;
  key: string;
  surchargeMinor: number;
  calculationValue: number;
  labels: Record<string, string>;
  descriptions: Record<string, string>;
}

export interface ConfiguratorEditorFieldDraft {
  clientId: string;
  key: string;
  type: ConfiguratorEditorFieldType;
  required: boolean;
  canonicalUnit: PublicConfiguratorCanonicalUnit;
  minimumScaled: number;
  maximumScaled: number;
  stepScaled: number;
  maximumLength: number;
  baseSurchargeMinor: number;
  variantIds: string[];
  labels: Record<string, string>;
  helpTexts: Record<string, string>;
  placeholders: Record<string, string>;
  choices: ConfiguratorEditorChoiceDraft[];
}

export interface ConfiguratorEditorTemplateDraft {
  clientId: string;
  key: string;
  variantId: string | null;
  mediaId: number | null;
  surchargeMinor: number;
  minimumWidthPpm: number;
  maximumWidthPpm: number;
  aspectWidth: number;
  aspectHeight: number;
  labels: Record<string, string>;
  descriptions: Record<string, string>;
}

export interface ConfiguratorEditorSurfaceDraft {
  clientId: string;
  key: string;
  variantId: string | null;
  baseMediaId: number | null;
  xPpm: number;
  yPpm: number;
  widthPpm: number;
  heightPpm: number;
  physicalWidthMm: number;
  physicalHeightMm: number;
  required: boolean;
  maximumLayers: number;
  labels: Record<string, string>;
  helpTexts: Record<string, string>;
  templates: ConfiguratorEditorTemplateDraft[];
}

export type ConfiguratorPricingFormulaDraft =
  | { mode: "none"; rateMinor: number; lengthKey: string; widthKey: string; heightKey: string }
  | { mode: "per-length"; rateMinor: number; lengthKey: string; widthKey: string; heightKey: string }
  | { mode: "per-area"; rateMinor: number; lengthKey: string; widthKey: string; heightKey: string }
  | { mode: "per-volume"; rateMinor: number; lengthKey: string; widthKey: string; heightKey: string };

export interface ProductConfiguratorEditorDraft {
  maxSurchargeMinor: number;
  fields: ConfiguratorEditorFieldDraft[];
  surfaces: ConfiguratorEditorSurfaceDraft[];
  pricingFormula: ConfiguratorPricingFormulaDraft;
  lengthFieldKey: string;
  widthFieldKey: string;
  heightFieldKey: string;
  weightFieldKey: string;
}

export interface ProductConfiguratorPublishPayload {
  productId: string;
  maxSurchargeMinor: number;
  fields: Array<Record<string, unknown>>;
  surfaces: Array<Record<string, unknown>>;
  pricingFormula: ConfiguratorPricingFormulaDraft;
  lengthFieldKey?: string;
  widthFieldKey?: string;
  heightFieldKey?: string;
  weightFieldKey?: string;
}

/** Strips browser-only keys before sending the exact configurator-store payload. */
export function buildProductConfiguratorPublishPayload(
  productId: string,
  draft: ProductConfiguratorEditorDraft,
): ProductConfiguratorPublishPayload {
  const fields = draft.fields.map(({ clientId: _clientId, choices, ...field }) => ({
    ...field,
    choices: field.type === "choice"
      ? choices.map(({ clientId: _choiceClientId, ...choice }) => choice)
      : [],
  }));
  const surfaces = draft.surfaces.map(({ clientId: _clientId, templates, ...surface }) => ({
    ...surface,
    baseMediaId: surface.baseMediaId,
    templates: templates.map(({ clientId: _templateClientId, ...template }) => ({ ...template, mediaId: template.mediaId })),
  }));
  return {
    productId,
    maxSurchargeMinor: draft.maxSurchargeMinor,
    fields,
    surfaces,
    pricingFormula: { ...draft.pricingFormula },
    ...(draft.lengthFieldKey ? { lengthFieldKey: draft.lengthFieldKey } : {}),
    ...(draft.widthFieldKey ? { widthFieldKey: draft.widthFieldKey } : {}),
    ...(draft.heightFieldKey ? { heightFieldKey: draft.heightFieldKey } : {}),
    ...(draft.weightFieldKey ? { weightFieldKey: draft.weightFieldKey } : {}),
  };
}
